import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ObjectPhoto } from '../../components/shared/ObjectPhoto';
import { ApiError } from '../../lib/api';
import { sanitizeCode, slugify } from '../../lib/codeUtils';
import {
  dictionariesApi,
  type DictionaryDetail,
  type DictionaryFieldDataType,
  type DictionaryFieldDetail,
  type DictionaryValue,
} from '../../lib/dictionariesApi';

type FieldState = DictionaryFieldDetail & { codeTouched?: boolean };
type Tab = 'fields' | 'values';

const dataTypeLabels: Record<DictionaryFieldDataType, string> = {
  string: 'Строка',
  text: 'Текст',
  integer: 'Целое',
  decimal: 'Дробное',
  boolean: 'Логич.',
  date: 'Дата',
  datetime: 'Дата+время',
};

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  return String(value);
}

export function DictionaryDetailPage() {
  const { dictionaryId } = useParams<{ dictionaryId: string }>();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('values');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dictName, setDictName] = useState('');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [fields, setFields] = useState<FieldState[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [values, setValues] = useState<DictionaryValue[]>([]);
  const [valuesLoading, setValuesLoading] = useState(true);
  const [valuesError, setValuesError] = useState<string | null>(null);
  const [newValueName, setNewValueName] = useState('');
  const [addingValue, setAddingValue] = useState(false);
  const [deletingValueId, setDeletingValueId] = useState<string | null>(null);

  const [editingCell, setEditingCell] = useState<{ valueId: string; code: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingName, setEditingNameId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState('');

  const loadDictionary = () => {
    if (!dictionaryId) return;
    setLoading(true);
    dictionariesApi
      .getDictionary(dictionaryId)
      .then((detail: DictionaryDetail) => {
        setDictName(detail.name);
        setName(detail.name);
        setCode(detail.code);
        setFields(detail.fields.map((f) => ({ ...f, codeTouched: true })));
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить справочник.'))
      .finally(() => setLoading(false));
  };

  const loadValues = () => {
    if (!dictionaryId) return;
    setValuesLoading(true);
    dictionariesApi
      .listValues(dictionaryId)
      .then(setValues)
      .catch((err) => setValuesError(err instanceof ApiError ? err.message : 'Не удалось загрузить значения.'))
      .finally(() => setValuesLoading(false));
  };

  useEffect(loadDictionary, [dictionaryId]);
  useEffect(loadValues, [dictionaryId]);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { code: `field_${prev.length + 1}`, name: '', data_type: 'string', required: false, codeTouched: false },
    ]);
  };
  const removeField = (index: number) => setFields((prev) => prev.filter((_, i) => i !== index));

  const handleSaveStructure = async () => {
    if (!dictionaryId) return;
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await dictionariesApi.updateDictionary(dictionaryId, {
        name: name.trim(),
        code: code.trim(),
        fields: fields.map(({ code: c, name: n, data_type, required }) => ({ code: c, name: n, data_type, required })),
      });
      setDictName(updated.name);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить справочник.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDictionary = async () => {
    if (!dictionaryId) return;
    if (!window.confirm(`Удалить справочник «${dictName}»? Все его значения тоже будут удалены.`)) return;
    try {
      await dictionariesApi.deleteDictionary(dictionaryId);
      navigate('/app/dictionaries');
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось удалить справочник.');
    }
  };

  const handleAddValue = async () => {
    if (!dictionaryId) return;
    if (!newValueName.trim()) {
      setValuesError('Сначала введите название значения');
      return;
    }
    setAddingValue(true);
    setValuesError(null);
    try {
      const created = await dictionariesApi.createValue(dictionaryId, newValueName.trim());
      setValues((prev) => [...prev, created]);
      setNewValueName('');
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось добавить значение.');
    } finally {
      setAddingValue(false);
    }
  };

  const handleSetPhoto = async (value: DictionaryValue, fileId: string) => {
    if (!dictionaryId) return;
    try {
      const updated = await dictionariesApi.setValuePhoto(dictionaryId, value.id, fileId);
      setValues((prev) => prev.map((v) => (v.id === value.id ? updated : v)));
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось сохранить фото.');
    }
  };

  const handleDeleteValue = async (value: DictionaryValue) => {
    if (!dictionaryId) return;
    if (!window.confirm(`Удалить значение «${value.name}»?`)) return;
    setDeletingValueId(value.id);
    try {
      await dictionariesApi.deleteValue(dictionaryId, value.id);
      setValues((prev) => prev.filter((v) => v.id !== value.id));
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось удалить значение.');
    } finally {
      setDeletingValueId(null);
    }
  };

  const commitName = async (value: DictionaryValue) => {
    if (!dictionaryId) return;
    const trimmed = editNameValue.trim();
    setEditingNameId(null);
    if (!trimmed || trimmed === value.name) return;
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, value.id, trimmed, value.properties);
      setValues((prev) => prev.map((v) => (v.id === value.id ? updated : v)));
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось сохранить название.');
    }
  };

  const commitCell = async (value: DictionaryValue, field: DictionaryFieldDetail) => {
    if (!dictionaryId) return;
    setEditingCell(null);
    let nextValue: unknown = editValue.trim() === '' ? null : editValue;
    if (nextValue !== null && field.data_type === 'integer') {
      if (!/^-?\d+$/.test(editValue.trim())) {
        setValuesError('Нужно целое число');
        return;
      }
      nextValue = Number(editValue.trim());
    } else if (nextValue !== null && field.data_type === 'decimal') {
      const n = Number(editValue.trim());
      if (Number.isNaN(n)) {
        setValuesError('Нужно число');
        return;
      }
      nextValue = n;
    }
    const nextProps = { ...value.properties, [field.code]: nextValue };
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, value.id, value.name, nextProps);
      setValues((prev) => prev.map((v) => (v.id === value.id ? updated : v)));
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось сохранить значение.');
    }
  };

  const toggleBoolean = async (value: DictionaryValue, field: DictionaryFieldDetail, checked: boolean) => {
    if (!dictionaryId) return;
    const nextProps = { ...value.properties, [field.code]: checked };
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, value.id, value.name, nextProps);
      setValues((prev) => prev.map((v) => (v.id === value.id ? updated : v)));
    } catch (err) {
      setValuesError(err instanceof ApiError ? err.message : 'Не удалось сохранить значение.');
    }
  };

  if (loading) return <p className="p-8 text-sm text-text-secondary">Загрузка…</p>;
  if (loadError) return <p className="p-8 text-sm font-medium text-status-danger">{loadError}</p>;

  return (
    <div className="flex h-screen w-full flex-col overflow-y-auto bg-bg-canvas">
      <header className="flex h-[72px] shrink-0 items-center gap-4 border-b border-border-default bg-bg-surface px-8">
        <Link to="/app/dictionaries" className="text-sm font-medium text-text-secondary hover:text-text-primary">
          ← Справочники
        </Link>
        <h1 className="text-[17px] font-semibold text-text-primary">{dictName}</h1>
        <button
          type="button"
          onClick={handleDeleteDictionary}
          className="ml-auto text-[13px] font-medium text-status-danger hover:underline"
        >
          Удалить справочник
        </button>
      </header>

      <div className="flex h-[54px] shrink-0 items-center gap-6 border-b border-border-default bg-bg-surface px-8">
        {(
          [
            ['values', 'Значения'],
            ['fields', 'Поля'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`h-full border-b-2 text-[13px] font-medium ${
              tab === key ? 'border-action-primary text-action-primary' : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'fields' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[900px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Название</h2>
            <label className="mt-4 text-[11px] font-medium text-text-secondary">Название справочника</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 h-10 w-full max-w-[420px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            />
            <label className="mt-3 block text-[11px] font-medium text-text-secondary">Код</label>
            <input
              value={code}
              onChange={(e) => setCode(sanitizeCode(e.target.value))}
              className="mt-1.5 h-10 w-full max-w-[420px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            />
          </div>

          <div className="mx-auto mt-6 max-w-[900px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-text-primary">Поля значений</h2>
              <button
                type="button"
                onClick={addField}
                className="flex h-9 shrink-0 items-center rounded-[9px] bg-action-primary px-3 text-xs font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                + Добавить поле
              </button>
            </div>

            <div className="mt-4 overflow-x-auto">
              <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 border-b border-border-default pb-2 text-[10px] font-semibold uppercase text-text-secondary">
                <span>Код</span>
                <span>Имя</span>
                <span>Тип</span>
                <span>Обяз.</span>
                <span />
              </div>
              <div className="mt-1 flex flex-col">
                <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 opacity-60">
                  <span className="truncate text-xs text-text-secondary">id</span>
                  <span className="truncate text-xs text-text-secondary">Идентификатор</span>
                  <span className="text-xs text-text-secondary">Системное</span>
                  <span />
                  <span />
                </div>
                <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 opacity-60">
                  <span className="truncate text-xs text-text-secondary">name</span>
                  <span className="truncate text-xs text-text-secondary">Название</span>
                  <span className="text-xs text-text-secondary">Строка</span>
                  <span />
                  <span />
                </div>
                {fields.length === 0 ? null : fields.map((field, i) => (
                    <div
                      key={i}
                      className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        value={field.code}
                        onChange={(e) => {
                          const c = sanitizeCode(e.target.value);
                          setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, code: c, codeTouched: true } : f)));
                        }}
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none"
                      />
                      <input
                        value={field.name}
                        onChange={(e) => {
                          const n = e.target.value;
                          setFields((prev) =>
                            prev.map((f, idx) =>
                              idx === i ? { ...f, name: n, code: f.codeTouched ? f.code : slugify(n, `field_${i + 1}`) } : f,
                            ),
                          );
                        }}
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none"
                      />
                      <select
                        value={field.data_type}
                        onChange={(e) =>
                          setFields((prev) =>
                            prev.map((f, idx) => (idx === i ? { ...f, data_type: e.target.value as DictionaryFieldDataType } : f)),
                          )
                        }
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none"
                      >
                        {(Object.entries(dataTypeLabels) as [DictionaryFieldDataType, string][]).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) =>
                          setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, required: e.target.checked } : f)))
                        }
                        className="mx-auto size-[18px] rounded-[5px] accent-action-primary"
                      />
                      <button
                        type="button"
                        onClick={() => removeField(i)}
                        title="Удалить поле"
                        className="mx-auto flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-status-danger"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {saveError && <p className="mx-auto mt-4 max-w-[900px] text-xs font-medium text-status-danger">{saveError}</p>}

          <div className="mx-auto mt-4 flex max-w-[900px] items-center gap-3">
            <button
              type="button"
              onClick={handleSaveStructure}
              disabled={!name.trim() || !code.trim() || saving}
              className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
            {saved && <span className="text-xs font-medium text-status-success">Сохранено</span>}
          </div>
        </div>
      )}

      {tab === 'values' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[1100px]">
            <div className="flex items-center gap-2">
              <input
                value={newValueName}
                onChange={(e) => setNewValueName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddValue()}
                placeholder="Название нового значения"
                className="h-10 w-72 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
              />
              <button
                type="button"
                onClick={handleAddValue}
                disabled={addingValue}
                className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {addingValue ? 'Добавляем…' : '+ Добавить значение'}
              </button>
            </div>

            {valuesError && <p className="mt-3 text-xs font-medium text-status-danger">{valuesError}</p>}

            <div className="mt-4 overflow-x-auto rounded-2xl border border-border-default bg-bg-surface">
              {valuesLoading ? (
                <p className="p-6 text-center text-xs text-text-secondary">Загрузка…</p>
              ) : values.length === 0 ? (
                <p className="p-6 text-center text-xs text-text-secondary">Значений пока нет</p>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead className="bg-bg-surface-secondary">
                    <tr>
                      <th className="w-[60px] px-3 py-2 font-semibold text-text-secondary">Фото</th>
                      <th className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">Название</th>
                      {fields.map((f) => (
                        <th key={f.code} className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">
                          {f.name}
                        </th>
                      ))}
                      <th className="w-20 px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {values.map((value) => (
                      <tr key={value.id} className="border-t border-border-default hover:bg-bg-surface-secondary">
                        <td className="px-3 py-1.5">
                          <ObjectPhoto
                            fileId={value.photo_file_id}
                            canEdit
                            size={40}
                            onChange={(fileId) => handleSetPhoto(value, fileId)}
                          />
                        </td>
                        <td
                          className="cursor-pointer px-3 py-1.5 font-medium text-text-primary hover:bg-bg-canvas"
                          onClick={() => {
                            setEditingNameId(value.id);
                            setEditNameValue(value.name);
                          }}
                        >
                          {editingName === value.id ? (
                            <input
                              autoFocus
                              value={editNameValue}
                              onChange={(e) => setEditNameValue(e.target.value)}
                              onBlur={() => commitName(value)}
                              onKeyDown={(e) => e.key === 'Enter' && commitName(value)}
                              className="h-7 w-full rounded border border-action-primary bg-bg-surface px-1.5 text-xs text-text-primary outline-none"
                            />
                          ) : (
                            value.name
                          )}
                        </td>
                        {fields.map((field) => {
                          const isEditing = editingCell?.valueId === value.id && editingCell.code === field.code;
                          const cellValue = value.properties[field.code];
                          if (field.data_type === 'boolean') {
                            return (
                              <td key={field.code} className="px-3 py-1.5">
                                <input
                                  type="checkbox"
                                  checked={Boolean(cellValue)}
                                  onChange={(e) => toggleBoolean(value, field, e.target.checked)}
                                  className="size-[16px] rounded-[4px] accent-action-primary"
                                />
                              </td>
                            );
                          }
                          return (
                            <td
                              key={field.code}
                              onClick={() => {
                                setEditingCell({ valueId: value.id, code: field.code });
                                setEditValue(formatCellValue(cellValue) === '—' ? '' : String(cellValue));
                              }}
                              className="cursor-pointer px-3 py-1.5 text-text-primary hover:bg-bg-canvas"
                            >
                              {isEditing ? (
                                <input
                                  autoFocus
                                  type={field.data_type === 'date' ? 'date' : field.data_type === 'datetime' ? 'datetime-local' : field.data_type === 'integer' || field.data_type === 'decimal' ? 'number' : 'text'}
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => commitCell(value, field)}
                                  onKeyDown={(e) => e.key === 'Enter' && commitCell(value, field)}
                                  className="h-7 w-full rounded border border-action-primary bg-bg-surface px-1.5 text-xs text-text-primary outline-none"
                                />
                              ) : (
                                formatCellValue(cellValue)
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/app/dictionaries/${dictionaryId}/values/${value.id}`}
                              title="Открыть карточку"
                              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-action-primary"
                            >
                              ➜
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDeleteValue(value)}
                              disabled={deletingValueId === value.id}
                              title="Удалить"
                              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-status-danger disabled:opacity-40"
                            >
                              {deletingValueId === value.id ? '…' : '🗑'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
